import {
  BodyType,
  Drivetrain,
  FuelType,
  Transmission,
  VehicleStatus,
} from '@ecowheels/types';

/**
 * Hungarian labels for the vehicle enums.
 *
 * The admin is Hungarian-only (unlike the public site, which translates these
 * through next-intl), so the labels live here as plain objects rather than in a
 * message catalogue. `Record<Enum, string>` is the point: adding a body type to
 * the shared enum without labelling it here is a compile error, not a dropdown
 * silently missing an option.
 */

export const STATUS_LABELS: Record<VehicleStatus, string> = {
  [VehicleStatus.DRAFT]: 'Piszkozat',
  [VehicleStatus.ACTIVE]: 'Meghirdetve',
  [VehicleStatus.RESERVED]: 'Lefoglalva',
  [VehicleStatus.SOLD]: 'Elkelt',
};

/** What each status means for the public site — shown next to the selector. */
export const STATUS_HINTS: Record<VehicleStatus, string> = {
  [VehicleStatus.DRAFT]: 'Nem látszik a weboldalon.',
  [VehicleStatus.ACTIVE]: 'Megjelenik a listában, érdeklődni lehet rá.',
  [VehicleStatus.RESERVED]: 'Látszik a listában „Lefoglalva” jelzéssel.',
  [VehicleStatus.SOLD]: 'Lekerül a listáról, de a saját linkje továbbra is működik.',
};

export const FUEL_LABELS: Record<FuelType, string> = {
  [FuelType.ELECTRIC]: 'Elektromos',
  [FuelType.PLUGIN_HYBRID]: 'Plug-in hibrid',
  [FuelType.HYBRID]: 'Hibrid',
  [FuelType.PETROL]: 'Benzin',
  [FuelType.DIESEL]: 'Dízel',
  [FuelType.LPG]: 'Benzin / LPG',
};

export const TRANSMISSION_LABELS: Record<Transmission, string> = {
  [Transmission.MANUAL]: 'Manuális',
  [Transmission.AUTOMATIC]: 'Automata',
};

export const DRIVETRAIN_LABELS: Record<Drivetrain, string> = {
  [Drivetrain.FWD]: 'Első kerék',
  [Drivetrain.RWD]: 'Hátsó kerék',
  [Drivetrain.AWD]: 'Összkerék',
};

export const BODY_LABELS: Record<BodyType, string> = {
  [BodyType.HATCHBACK]: 'Ferdehátú',
  [BodyType.SEDAN]: 'Szedán',
  [BodyType.WAGON]: 'Kombi',
  [BodyType.SUV]: 'SUV / Terepjáró',
  [BodyType.MPV]: 'Egyterű',
  [BodyType.COUPE]: 'Kupé',
  [BodyType.CABRIO]: 'Kabrió',
  [BodyType.VAN]: 'Zárt dobozos',
  [BodyType.PICKUP]: 'Pickup',
};

/** Turns a label map into `<option>` data, preserving the enum's order. */
export function options<T extends string>(labels: Record<T, string>) {
  return (Object.entries(labels) as [T, string][]).map(([value, label]) => ({ value, label }));
}

export const STATUS_TONE: Record<VehicleStatus, 'neutral' | 'success' | 'warning' | 'muted'> = {
  [VehicleStatus.DRAFT]: 'neutral',
  [VehicleStatus.ACTIVE]: 'success',
  [VehicleStatus.RESERVED]: 'warning',
  [VehicleStatus.SOLD]: 'muted',
};

/** "2021 Nissan Leaf 40 kWh Tekna" — the car's name wherever one is needed. */
export function vehicleTitle(vehicle: {
  year: number;
  make: string;
  model: string;
  variant?: string | null;
}): string {
  return [vehicle.year, vehicle.make, vehicle.model, vehicle.variant].filter(Boolean).join(' ');
}

/**
 * Where the public site lives, for "open this listing" links.
 *
 * These have to be absolute: the admin is served from its own host
 * (admin.ecowheels.hu vs ecowheels.hu — see docker/Caddyfile), so a relative
 * `/hasznaltautok/...` would hit the admin's own SPA fallback and just render
 * the admin again.
 *
 * Three sources, in order, so no single missing setting breaks the link:
 *
 *  1. `VITE_PUBLIC_SITE_URL`, baked in at build time (vite.config.ts).
 *  2. The admin's own origin with a leading `admin.` stripped. Production is
 *     laid out exactly that way, which makes the build-time variable a
 *     convenience rather than load-bearing — forgetting to set it used to
 *     leave production links silently pointing at `http://localhost:4200`.
 *  3. The dev default.
 */
function resolvePublicSiteUrl(): string {
  const configured = import.meta.env.VITE_PUBLIC_SITE_URL?.trim();
  // An unset Docker build arg arrives as '' rather than undefined, so this
  // checks for a non-empty value, not merely a defined one.
  if (configured) return configured.replace(/\/+$/, '');

  if (typeof window !== 'undefined') {
    const { protocol, host } = window.location;
    if (host.startsWith('admin.')) return `${protocol}//${host.slice('admin.'.length)}`;
  }

  return 'http://localhost:4200';
}

const PUBLIC_SITE_URL = resolvePublicSiteUrl();

/**
 * The Hungarian path is hardcoded: `localePrefix: 'as-needed'` means the
 * default locale is served without a prefix, so this is the canonical URL.
 */
export function publicVehicleUrl(slug: string): string {
  return `${PUBLIC_SITE_URL}/hasznaltautok/${slug}`;
}

/** 4900000 → "4 900 000 Ft"; null → "Ár kérésre" (never "0 Ft"). */
export function formatPrice(priceHuf: number | null): string {
  if (priceHuf === null) return 'Ár kérésre';
  return `${priceHuf.toLocaleString('hu-HU')} Ft`;
}

export function formatMileage(mileageKm: number): string {
  return `${mileageKm.toLocaleString('hu-HU')} km`;
}
